import { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useSignal, VegaChartSignal } from '@asset-metrix/portal-ui';
import { Spec, View } from 'vega';

import { usePersistentPagination } from '../../hooks/usePersistentPagination';
import {
  getFullscreenPageBoundaries,
  getPageBoundaries,
} from '../../utils/vegaChartHelpers';

export const PAGINATION_ENTRIES_DATA_NAME = 'paginationEntries';

export const useWithPagination = (spec: Spec) =>
  useMemo(
    () =>
      spec.signals?.some((s) => s.name === VegaChartSignal.pageBoundaries) ??
      false,
    [spec]
  );

const hasCustomPages = (spec: Spec) =>
  spec.data?.find((d) => d.name === PAGINATION_ENTRIES_DATA_NAME);

export const useItemCount = (
  spec: Spec,
  vegaViewRef: RefObject<View | null>,
  data?: unknown
) => {
  const [itemCount, setItemCount] = useState<number>();

  const listener = useCallback((_: unknown, newData: unknown) => {
    if (Array.isArray(newData)) {
      setItemCount(newData.length);
      return;
    }
    setItemCount(undefined);
  }, []);

  useEffect(() => {
    const currentRef = vegaViewRef.current;

    if (hasCustomPages(spec)) {
      currentRef?.addDataListener(PAGINATION_ENTRIES_DATA_NAME, listener);
    }

    return () => {
      if (hasCustomPages(spec)) {
        currentRef?.removeDataListener(PAGINATION_ENTRIES_DATA_NAME, listener);
      }
    };
  }, [listener, vegaViewRef, spec]);

  const onReadyCallback = useCallback(
    async (newView?: View) => {
      // the ref is only assigned once react-vega has mounted the view, so the
      // view handed over by the ready callback takes precedence
      const view = newView ?? vegaViewRef.current;

      if (hasCustomPages(spec)) {
        // listeners do not get called for initial internal calculation of the data
        await view?.runAsync();
        setItemCount(view?.data(PAGINATION_ENTRIES_DATA_NAME)?.length);

        // renew listeners when the callback is called. this happens sometimes after the initial render (or after theme switch).
        // the effects will trigger the first time before the vegaViewRef.current is set
        newView?.addDataListener(PAGINATION_ENTRIES_DATA_NAME, listener);
      } else if (Array.isArray(data)) {
        setItemCount(data.length);
      } else {
        setItemCount(undefined);
      }
    },
    [data, listener, spec, vegaViewRef]
  );

  useEffect(() => {
    // also call onReadyCallback when its dependencies change, in case the onReady call of the vega view has already happened previously
    onReadyCallback();
  }, [onReadyCallback]);

  return { itemCount, onReadyCallback };
};

export const usePageBoundaries = (
  withPagination: boolean,
  isFullscreen: boolean,
  vegaViewRef: RefObject<View | null>,
  spec: Spec,
  rawData: unknown,
  pageFilterInstanceId: string,
  widgetFilterInstanceId: string
) => {
  const signal = useSignal(vegaViewRef);

  const { currentPage, setCurrentPage } = usePersistentPagination(
    pageFilterInstanceId,
    widgetFilterInstanceId
  );

  const { itemCount, onReadyCallback: onReadyCallbackForItemCount } =
    useItemCount(spec, vegaViewRef, rawData);

  const specHasPageBoundarySignal = useWithPagination(spec);

  const pageBoundaries = useMemo(
    () => getPageBoundaries(currentPage, itemCount),
    [currentPage, itemCount]
  );

  const resolvedItemCount = itemCount ?? 0;

  const applyPageBoundaries = useCallback(() => {
    if (!specHasPageBoundarySignal) {
      return;
    }
    if (isFullscreen) {
      signal(
        VegaChartSignal.pageBoundaries,
        getFullscreenPageBoundaries(resolvedItemCount)
      );
    } else if (withPagination) {
      signal(VegaChartSignal.pageBoundaries, pageBoundaries);
    }
  }, [
    isFullscreen,
    pageBoundaries,
    resolvedItemCount,
    signal,
    specHasPageBoundarySignal,
    withPagination,
  ]);

  // Fullscreen shows all entries via pageBoundaries; paginated mode slices by page.
  // withPagination controls the paginator UI; specHasPageBoundarySignal controls the Vega signal.
  useEffect(() => {
    applyPageBoundaries();
  }, [applyPageBoundaries]);

  const onReadyCallback = useCallback(
    (newView: View) => {
      // The effect above already ran before the view existed, so the boundaries
      // are never applied to a freshly mounted view otherwise. Queueing this
      // before awaiting the item count keeps a page change made in the meantime
      // ordered after this update, so the user selection wins.
      applyPageBoundaries();

      // Once the item count is resolved the effect re-applies the boundaries
      // with the final count.
      return onReadyCallbackForItemCount(newView);
    },
    [applyPageBoundaries, onReadyCallbackForItemCount]
  );

  return {
    onReadyCallback,
    currentPage,
    setCurrentPage,
    itemCount: resolvedItemCount,
  };
};
